import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LegalContent } from "@/components/LegalContent";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

// One-time (per policy version) full-screen gate. Shown after login to any
// approved user whose agreed_version doesn't match the current POLICY_VERSION.
// The only way forward is to agree; a small Sign out link avoids trapping anyone.
export default function LegalGate() {
  const { setUser, logout } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    if (!agreed || busy) return;
    setBusy(true);
    try {
      const d = await api.acceptLegal();
      setUser(d.user);
    } catch {
      toast.error("Couldn't save your agreement — please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden" data-testid="legal-gate">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/logo.jpg)" }} />
      <div className="absolute inset-0 plaid-veil" />

      <div className="relative flex min-h-screen items-center justify-center px-5 py-10">
        <div className="lg-glass-strong flex w-full max-w-lg flex-col rounded-3xl p-6">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#9B2226]/20 text-[#E0575B]">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="font-display text-lg font-800 tracking-tight text-zinc-50">Before you continue</h1>
              <p className="text-sm text-zinc-300">Please review and agree to keep using Stage Door.</p>
            </div>
          </div>

          <div className="max-h-[46vh] overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950/40 p-4" data-testid="legal-gate-scroll">
            <LegalContent />
          </div>

          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-3.5" data-testid="legal-agree-row">
            <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5 data-[state=checked]:border-[#9B2226] data-[state=checked]:bg-[#9B2226]" data-testid="legal-agree-checkbox" />
            <span className="text-sm text-zinc-200">I have read and agree to the Terms of Service and Privacy Policy.</span>
          </label>

          <Button
            onClick={accept}
            disabled={!agreed || busy}
            className="mt-4 h-12 w-full bg-[#9B2226] text-base font-600 text-white hover:bg-[#7A1A1E] disabled:opacity-50"
            data-testid="legal-agree-continue"
          >
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</> : "Agree & Continue"}
          </Button>

          <p className="mt-4 text-center text-xs text-zinc-400">
            Not ready?{" "}
            <button onClick={() => logout()} className="font-600 text-zinc-300 underline hover:text-zinc-100" data-testid="legal-signout">
              Sign out
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
